/**
 * Mirroring, for building symmetric ships.
 *
 * Almost every ship anyone designs is symmetric, and building each half by hand
 * is tedious and easy to get subtly wrong — a thruster a quarter-cell out on one
 * side is invisible in the editor and permanent once flown.
 *
 * The reflection is about a ship-local axis through the origin, which is where
 * a design is centred anyway, and ships are drawn nose-up — so the mirror line
 * is the vertical one running fore and aft through the hull's centre, and
 * reflecting swaps port and starboard.
 *
 * There is only one axis on purpose. A fore-and-aft mirror is not a thing
 * anyone wants for a ship with a nose, and offering it only invited picking the
 * wrong one.
 */
import { ROTATION_STEP } from './placement.js';
/** Reflect a point across the ship's centreline: port becomes starboard. */
export function mirrorPoint(point) {
    return { x: -point.x, y: point.y };
}
/**
 * Reflect an orientation across the centreline.
 *
 * A reflection is not a rotation, so this is emphatically not "add four
 * eighths" — that would turn every mirrored part to face backwards. Reflecting
 * a direction about a vertical line sends angle θ to 180° − θ, which leaves
 * anything pointing straight fore or aft untouched and swaps left-lean for
 * right-lean. Measuring from straight up, a part leaning +45° comes out at −45°.
 */
export function mirrorFacing(facing) {
    const steps = Math.round((2 * Math.PI) / ROTATION_STEP);
    return (((steps / 2 - facing) % steps) + steps) % steps;
}
/**
 * The angle a component's mesh must actually be drawn at, in radians.
 *
 * `facing` is the direction the part *works* in — where a thruster pushes —
 * and for a mirrored part that is the reflected direction, 180° − θ. But a
 * mirrored mesh is also flipped, and a flip composed with a rotation is not the
 * rotation you started with: reflecting and then turning by 180° − θ comes out
 * as a reflection about the wrong axis, which is why mirrored parts looked
 * reversed however the facing was computed.
 *
 * Working it through: three.js applies scale before rotation, so a flipped mesh
 * gives R(rot)·Reflect. The transform wanted is Reflect·R(θ), and the identity
 * Reflect·R(θ) = R(−θ)·Reflect makes rot = −θ. Substituting θ = 180° − facing
 * leaves rot = facing − 180°, which in eighths is facing + 4.
 *
 * Kept here, next to the reflection it undoes, so the renderer cannot drift
 * from it.
 */
export function renderFacing(component) {
    const facing = component.facing ?? 0;
    const steps = Math.round((2 * Math.PI) / ROTATION_STEP);
    const turns = component.mirrored ? facing + steps / 2 : facing;
    return (turns % steps) * ROTATION_STEP;
}
/** Is this point on the centreline, and therefore its own reflection? */
export function onAxis(point, tolerance = 1e-9) {
    return Math.abs(point.x) <= tolerance;
}
/**
 * The reflection of a line, or `null` when it lies along the axis.
 *
 * A line straddling the centreline is its own mirror, and placing it twice
 * would put two lines in the same space — which the structure rules correctly
 * reject as a wall built twice.
 */
export function mirrorSegment(a, b) {
    if (onAxis(a) && onAxis(b))
        return null;
    return { a: mirrorPoint(a), b: mirrorPoint(b) };
}
/**
 * The reflection of a component, or `null` when it is centred on the axis.
 *
 * A part straddling the centreline is already symmetric; duplicating it would
 * just overlap itself.
 */
export function mirrorComponent(component, id) {
    if (onAxis(component.position))
        return null;
    return {
        ...component,
        id,
        position: mirrorPoint(component.position),
        facing: mirrorFacing(component.facing ?? 0),
        // A reflected part is a mirror image, not a turned copy. Without this the
        // mesh is merely rotated into place, which is wrong for anything that is
        // not left-right symmetric and reads as a part spun the wrong way round.
        mirrored: !component.mirrored,
    };
}
//# sourceMappingURL=mirror.js.map